import { clsx } from 'clsx';
import { Heart } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';
import { useIsWishlisted, useToggleWishlist } from '@/lib/wishlist';

export interface WishlistButtonProps {
  productId: string;
  /** Product name, for the accessible label. */
  name: string;
  className?: string;
  /** A pill with a visible "Save" / "Saved" label (the product page) instead of the round heart. */
  labelled?: boolean;
}

/**
 * Heart toggle (`aria-pressed`). The label stays the same ("Save X to your wishlist") and only the
 * pressed state changes — the pattern screen readers announce best. Works signed out too: the heart
 * is kept on this device and joins the account at sign-in (lib/wishlist).
 */
export function WishlistButton({
  productId,
  name,
  className,
  labelled = false,
}: WishlistButtonProps) {
  const t = useT();
  const saved = useIsWishlisted(productId);
  const toggle = useToggleWishlist();
  // the little pop plays for a heart the visitor just pressed, not for ones that were already saved
  const [pressed, setPressed] = useState(false);

  const common = {
    className: clsx('wish', className),
    'aria-label': t('product.wishlistLabel', { name }),
    'aria-pressed': saved,
    'data-saved': saved,
    'data-pop': pressed && saved,
    onClick: () => {
      setPressed(true);
      toggle(productId, !saved);
    },
  };

  return labelled ? (
    <Button variant="glass" {...common}>
      <Heart size={20} aria-hidden="true" />
      <span aria-hidden="true">{saved ? t('product.saved') : t('product.save')}</span>
    </Button>
  ) : (
    <Button variant="icon" {...common}>
      <Heart size={20} aria-hidden="true" />
    </Button>
  );
}
